import { localFormat } from '@lmt/unicorn-shared/app/helpers/date-helper';
import { MaybeUndefined } from '@lmt/unicorn-shared/app/helpers/typeUtils';
import clsx from 'clsx';
import { addMilliseconds, startOfToday } from 'date-fns';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ComboBoxInput } from '../dropdown/ComboBoxInput2';

import styles from './TimePicker.module.scss';

interface TimePickerProps {
  minTimeInMills?: number;
  timeInMills: number | null | undefined;
  onTimeChanged?: (newTime: number | null, isInvalid: boolean) => void;
  error?: boolean;
  errorText?: string;
  timeEntriesIntervalInMinutes?: number;
  className?: string;
  variant?: 'normal' | 'formInput';

  /*
  Called when user typed something wrong (e.g. 'asd') and leaves the field (onBlur).
  Form that owns this control shall account the error state for validation purposes.
  When user corrects his input, `onTimeChanged` will be called.
   */
  onInvalidTime?: () => void;
  testId?: string;
  disabled?: boolean;
}

interface TimeEntry {
  timeInMills: number;
  label: string;
}

function parseTime(timeString: string): number | null {
  if (timeString == '') return null;

  const time = timeString.match(/(\d+)(:(\d\d))?/i);
  const hasAmPm =
    timeString.toLowerCase().includes('am') ||
    timeString.toLowerCase().includes('pm');
  const hasPm = timeString.toLowerCase().includes('pm');
  if (time == null) return null;

  let hours = parseInt(time[1], 10);
  if (hours == 12 && hasAmPm && !hasPm) {
    hours = 0;
  } else {
    hours += hours < 12 && hasPm ? 12 : 0;
  }
  const minutes = parseInt(time[3], 10) || 0;

  if (hours < 0 || hours > 24 || minutes < 0 || minutes > 60) {
    return null;
  }

  //convert to milliseconds
  return (hours * 60 + minutes) * 60000;
}

function millsToTimeString<T extends number | undefined | null>(
  timeInMills: T,
): string | MaybeUndefined<T> {
  if (timeInMills === null) return null as MaybeUndefined<T>;
  if (timeInMills === undefined) return undefined as MaybeUndefined<T>;

  return localFormat(addMilliseconds(startOfToday(), timeInMills), 'p');
}

export const TimePicker: FC<TimePickerProps> = (props) => {
  const {
    minTimeInMills,
    onTimeChanged,
    timeInMills,
    errorText,
    timeEntriesIntervalInMinutes = 30,
    className,
    variant,
  } = props;
  const i18n = useTranslation();
  const [isInvalidTimeError, setIsInvalidTimeError] = useState(false);
  // If set, this error is displayed instead of props.error.
  // Is used for Invalid Time error
  const [overriddenError, setOverriddenError] = useState<string | null>(null);

  useEffect(
    function resetOverriddenErrorWhenValidationSucceeds() {
      if (!isInvalidTimeError) {
        setOverriddenError(null);
      }
    },
    [isInvalidTimeError],
  );

  const timeEntries: TimeEntry[] = useMemo(() => {
    const key = timeEntriesIntervalInMinutes + i18n.i18n.language;
    if (!timeIntervalsCached[key]) {
      timeIntervalsCached[key] = generateTimeEntriesForWholeDay(
        timeEntriesIntervalInMinutes,
      );
    }
    return timeIntervalsCached[key].filter(
      (entry) => entry.timeInMills >= (minTimeInMills || 0),
    );
  }, [minTimeInMills, i18n.i18n.language]);

  const onChange = useCallback(
    (value: string | TimeEntry | null) => {
      setOverriddenError(null);
      if (value === null) {
        onTimeChanged?.(null, false);
      } else if (typeof value === 'string') {
        let newTime = parseTime(value);
        if (
          minTimeInMills !== undefined &&
          newTime !== null &&
          newTime < minTimeInMills
        ) {
          newTime = null;
        }
        if (newTime === null) {
          // time is invalid
          setIsInvalidTimeError(true);
          setOverriddenError(i18n.t('uikit.inputs.time_input.invalid'));
          onTimeChanged?.(null, true);
          props.onInvalidTime?.();
        } else {
          onTimeChanged?.(newTime, false);
        }
      } else {
        onTimeChanged?.(value.timeInMills, false);
      }
    },
    [onTimeChanged, minTimeInMills],
  );
  // const defaultSelectedTimeEntry = useMemo(() => {
  //   return timeEntries.find((x) => x.timeInMills === timeInMills);
  // }, [timeEntries, timeInMills]);
  return (
    <div className={clsx(styles.container, className)}>
      <ComboBoxInput
        options={timeEntries}
        getOptionLabel={'label'}
        value={
          timeEntries.find((x) => x.timeInMills === timeInMills) ??
          (timeInMills ? millsToTimeString(timeInMills) : '')
        }
        isOptionEqualToValue={(item1: TimeEntry, item2: TimeEntry) =>
          item1?.timeInMills === item2?.timeInMills
        }
        variant={variant ?? 'formInput'}
        errorText={props.disabled ? undefined : overriddenError ?? errorText}
        onValueChanged={onChange}
        testId={props.testId}
        disabled={props.disabled}
      />
    </div>
  );
};

const timeIntervalsCached: Record<string, TimeEntry[]> = {};

function generateTimeEntriesForWholeDay(
  timeIntervalInMinutes: number,
): TimeEntry[] {
  const totalMillsInADay = 24 * 60 * 60000;
  const intervalInMills = timeIntervalInMinutes * 60000;
  const entries: TimeEntry[] = [];
  for (
    let timeInMills = 0;
    timeInMills < totalMillsInADay;
    timeInMills += intervalInMills
  ) {
    const label = millsToTimeString(timeInMills);
    entries.push({
      timeInMills,
      label: label,
    });
  }
  return entries;
}
